import { ColumnDef } from "@tanstack/react-table";
import cn from "classnames";
import { getFormattedUTCDate } from "helpers";
import { useLanguage } from "hooks";
import { OrderPaymentMethod } from "modules/orders";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ItemsTable, Status, TableItemStatus } from "ui";

import {
  ADMIN_DELIVERIES_LABELS,
  DELIVERY_STATUSES_LIST,
  DeliveryType
} from "../../constants";
import { DriverDeliveriesListItem } from "../../financial.types";
import { OrderCell, ActionCell } from "./atoms";
import { DeliveriesTableProps } from "./DeliveriesTable.types";

import styles from "./DeliveriesTable.module.scss";

const DeliveriesTable = ({
  items,
  isLoading,
  sorting,
  setSorting,
  refetchDeliveries,
  labels = ADMIN_DELIVERIES_LABELS,
  hideActions = false
}: DeliveriesTableProps) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const statusesList: Status[] = DELIVERY_STATUSES_LIST.map((orStatus) => ({
    ...orStatus,
    onClick: () => {}
  }));

  const getOrderStatusIndex = (st: string) =>
    statusesList.findIndex((status) => status.value === st);

  const columns: Array<
    ColumnDef<DriverDeliveriesListItem, DriverDeliveriesListItem[]>
  > = useMemo(
    () => [
      {
        id: "OrderId",
        accessorKey: "OrderId",
        header: t("Order"),
        size: 14,
        minSize: 14,
        cell: ({ row }) => <OrderCell item={items[row.index]} />
      },

      {
        id: "DeliveryDate",
        accessorKey: "DeliveryDate",
        header: t("Delivery date"),
        size: 13,
        minSize: 13,
        cell: ({ row }) =>
          getFormattedUTCDate(items[row.index].deliveryDate, "DD/MM/YYYY")
      },

      {
        header: t("Order Total"),
        size: 10,
        minSize: 10,
        cell: ({ row }) => (
          <div
            className={cn({
              [styles.deliveries_returnCell]:
                items[row.index].type !== DeliveryType.Delivery
            })}
          >
            {items[row.index].totalOrder.toLocaleString("en-US")}
          </div>
        )
      },

      {
        header: t("Fees"),
        size: 8,
        minSize: 8,
        cell: ({ row }) => items[row.index].deliveryFee.toLocaleString("en-US")
      },

      {
        header: t(labels.toCollect),
        size: 9,
        minSize: 9,
        cell: ({ row }) => (
          <div
            className={cn({
              [styles.deliveries_boldText]: !!items[row.index].amountToCollect,
              [styles.deliveries_boldGreenText]:
                !!items[row.index].amountCollected
            })}
          >
            {(
              items[row.index].amountToCollect ||
              items[row.index].amountCollected
            ).toLocaleString("en-US")}
          </div>
        )
      },

      {
        header: t(labels.toPay),
        size: 8,
        minSize: 8,
        cell: ({ row }) => (
          <div
            className={cn({
              [styles.deliveries_boldText]: !!items[row.index].amountToPay
            })}
          >
            {items[row.index].amountToPay.toLocaleString("en-US")}
          </div>
        )
      },

      {
        header: t(labels.paid),
        size: 8,
        minSize: 8,
        cell: ({ row }) => (
          <div
            className={cn({
              [styles.deliveries_boldGreenText]: !!items[row.index].amountPaid
            })}
          >
            {items[row.index].amountPaid.toLocaleString("en-US")}
          </div>
        )
      },

      {
        header: t("Payment Method"),
        size: 10,
        minSize: 10,
        cell: ({ row }) =>
          items[row.index].orderPaymentMethod ===
          OrderPaymentMethod.CashOnDelivery
            ? t("Cash")
            : t("Mobile Money")
      },

      {
        header: t("Status"),
        size: 10,
        minSize: 10,
        cell: ({ row }) => (
          <TableItemStatus
            activeStatusIndex={getOrderStatusIndex(items[row.index].status)}
            statusesList={statusesList}
          />
        )
      },

      ...(hideActions
        ? []
        : [
            {
              header: t("Action"),
              enableSorting: false,
              size: 7,
              minSize: 7,
              cell: ({ row }: { row: { index: number } }) => (
                <ActionCell
                  refetchDeliveries={refetchDeliveries}
                  item={items[row.index]}
                />
              )
            }
          ])
    ],
    [items, currentLanguage, hideActions]
  );

  const returnRowsIndexes = items
    .map((item, index) =>
      item.type !== DeliveryType.Delivery ? index : undefined
    )
    .filter((item) => item !== undefined);

  return (
    <ItemsTable
      columns={columns}
      data={items}
      sorting={sorting}
      skeletonHeight={65}
      setSorting={setSorting}
      isLoading={isLoading}
      className={styles.deliveries}
      specificRowsIndexes={returnRowsIndexes as number[]}
      specificRowsClassName={styles.deliveries_orangeRow}
      rowsClassName={styles.deliveries_tableRow}
      headerRowClassName={styles.deliveries_tableHeaderRow}
    />
  );
};

export default DeliveriesTable;
